import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App } from "antd";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import { Button } from "../../../custom";
import { formatCurrency } from "../../../utils/currency";
import { routes } from "../../../routes";
import styles from "./style.module.scss";

const REMITA_SESSION_KEY = "remita-pending-payment";
const remitaScriptCache: string[] = [];

const getInitialData = (
  state: RemitaSummaryPaymentState | undefined,
): RemitaSummaryPaymentState | null => {
  if (state?.rrr) {
    sessionStorage.setItem(REMITA_SESSION_KEY, JSON.stringify(state));
    return state;
  }

  const stored = sessionStorage.getItem(REMITA_SESSION_KEY);
  return stored ? (JSON.parse(stored) as RemitaSummaryPaymentState) : null;
};

const RemitaSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const [data] = useState<RemitaSummaryPaymentState | null>(() =>
    getInitialData(location.state as RemitaSummaryPaymentState | undefined),
  );
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!data) {
      return;
    }

    const scriptSrc = data.isLive
      ? "https://login.remita.net/payment/v1/remita-pay-inline.bundle.js"
      : "https://demo.remita.net/payment/v1/remita-pay-inline.bundle.js";

    if (window.RmPaymentEngine || remitaScriptCache.includes(scriptSrc)) {
      setIsScriptLoaded(true);
      return;
    }

    remitaScriptCache.push(scriptSrc);

    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;

    const handleLoad = () => setIsScriptLoaded(true);
    const handleError = () => {
      const index = remitaScriptCache.indexOf(scriptSrc);
      if (index >= 0) {
        remitaScriptCache.splice(index, 1);
      }
      setIsScriptLoaded(false);
      notification.error({
        message: "Remita Error",
        description: "Unable to load the Remita payment script.",
      });
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, [data, notification]);

  const handleProceed = () => {
    if (!data || !window.RmPaymentEngine) {
      notification.error({
        message: "Remita Error",
        description: "Remita is not ready yet. Please try again in a moment.",
      });
      return;
    }

    setIsProcessing(true);

    const handler = window.RmPaymentEngine.init({
      key: data.key,
      customerId: data.customerId,
      transactionId: data.rrr,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      amount: data.amount,
      narration: data.narration,
      processRrr: true,
      extendedData: {
        customFields: [{ name: "rrr", value: data.rrr }],
      },
      onSuccess: () => {
        setIsProcessing(false);
        sessionStorage.removeItem(REMITA_SESSION_KEY);
        navigate(`${routes.dashboard.payments}?ref=${data.paymentReference}`);
      },
      onError: (response) => {
        setIsProcessing(false);
        notification.error({
          message: "Remita Error",
          description:
            (response as { message?: string })?.message ||
            "Remita payment could not be completed.",
        });
      },
      onClose: () => {
        setIsProcessing(false);
      },
    });

    if (handler.showPaymentWidget) {
      handler.showPaymentWidget();
    } else {
      handler.openIframe?.();
    }
  };

  if (!data) {
    return (
      <PageLayout heading="Remita Payment Summary">
        <p>No pending Remita payment was found.</p>
        <Button
          text="Back to Payments"
          onClick={() => navigate(routes.dashboard.payments)}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout heading="Remita Payment Summary">
      <div className={styles.receiptContainer}>
        <div className={styles.grid}>
          <div className={styles.receiptDetails}>
            <label>Name</label>
            <h6>{`${data.firstName} ${data.lastName}`.trim()}</h6>
          </div>
          <div className={styles.receiptDetails}>
            <label>Email</label>
            <h6>{data.email}</h6>
          </div>
          {data.phone && (
            <div className={styles.receiptDetails}>
              <label>Phone</label>
              <h6>{data.phone}</h6>
            </div>
          )}
          <div className={styles.receiptDetails}>
            <label>Amount</label>
            <h6>{formatCurrency(data.amount)}</h6>
          </div>
          <div className={styles.receiptDetails}>
            <label>Remita Retrieval Reference (RRR)</label>
            <h6>{data.rrr}</h6>
          </div>
        </div>
      </div>
      <br />
      <Button
        text="Proceed to Pay"
        onClick={handleProceed}
        isLoading={isProcessing}
        disabled={!isScriptLoaded}
      />
    </PageLayout>
  );
};

export default RemitaSummary;
